import { useState, useEffect } from "react";
import { NavLink, useLocation, useNavigate } from "react-router";
import { useTranslation } from "react-i18next";
import {
  User, LayoutDashboard, FileText, Database, ShieldCheck, ClipboardList, LogOut, Receipt, Network,
  MoreHorizontal, ChevronRight,
} from "lucide-react";
import MeditationIcon from "../../../../shared/ui/icons/MeditationIcon";
import { getPortalMe, signOutPatient } from "../../application/portalAccount";
import LanguageSelector from "../../../../shared/ui/header/LanguageSelector";
import { Dropdown } from "../../../../shared/ui/dropdown/Dropdown";
import { Modal } from "../../../../shared/ui/modal";
import {
  getPaymentsAvailability,
  listPortalMeditations,
  listPortalSystemicMaps,
} from "../../application/portalServices";
import PortalPanelSwitcher from "./PortalPanelSwitcher";

type NavItem = {
  to: string;
  label: string;
  icon: React.ComponentType<{ size?: number; className?: string }>;
  end: boolean;
};

// What a patient opens the portal for, between one session and the next.
// These are the only entries that own a slot in the phone tab bar.
const PRIMARY_ITEMS: NavItem[] = [
  { to: "/portal",            label: "portal.nav.dashboard",  icon: LayoutDashboard, end: true },
  { to: "/portal/agreements", label: "portal.nav.agreements", icon: ClipboardList,   end: false },
  { to: "/portal/notes",      label: "portal.nav.notes",      icon: FileText,        end: false },
];

// Shown only once the therapist has handed over a meditation. An entry that is
// always there and always empty teaches the patient to ignore it.
const MEDITATIONS_ITEM: NavItem = {
  to: "/portal/meditations",
  label: "portal.nav.meditations",
  // The same figure the therapist's own menu uses for the tool: one tool,
  // one mark, whichever side of it you are on.
  icon: MeditationIcon,
  end: false,
};

// Shown only once the therapist has shared a map, for the same reason.
const SYSTEMIC_MAPS_ITEM: NavItem = {
  to: "/portal/systemic-maps",
  label: "portal.nav.systemicMaps",
  icon: Network,
  end: false,
};

// Shown only when the therapist takes payments through Alchely.
const PAYMENTS_ITEM: NavItem = {
  to: "/portal/payments",
  label: "portal.nav.payments",
  icon: Receipt,
  end: false,
};

// Paperwork a patient visits once in a long while. It stays reachable, but
// apart from the entries used every week.
const ACCOUNT_ITEMS: NavItem[] = [
  { to: "/portal/data",    label: "portal.nav.data",    icon: Database,    end: false },
  { to: "/portal/consent", label: "portal.nav.consent", icon: ShieldCheck, end: false },
];

const SIDEBAR_W = "w-[240px]";

const isOn = (pathname: string, item: NavItem) =>
  item.end ? pathname === item.to : pathname.startsWith(item.to);

/**
 * The frame around every authenticated portal page.
 *
 * Mounted once by the route that holds the pages, so moving between them keeps
 * the menu as it is instead of asking the server again what it should show.
 */
export default function PortalLayout({ children }: { children: React.ReactNode }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const [mobileDropdownOpen, setMobileDropdownOpen] = useState(false);
  const [desktopDropdownOpen, setDesktopDropdownOpen] = useState(false);
  const [moreOpen, setMoreOpen] = useState(false);
  const [patientName, setPatientName] = useState<string | undefined>(undefined);
  const [paymentsEnabled, setPaymentsEnabled] = useState(false);
  const [hasSystemicMaps, setHasSystemicMaps] = useState(false);
  const [hasMeditations, setHasMeditations] = useState(false);

  useEffect(() => {
    getPortalMe()
      .then((data) => setPatientName(`${data.first_name} ${data.last_name}`))
      .catch(() => {});
  }, []);

  // The server decides whether this patient has payments to look at; the
  // portal never infers it. A failure leaves the entry hidden, which is the
  // same as the therapist not using the feature.
  useEffect(() => {
    getPaymentsAvailability()
      .then((data) => setPaymentsEnabled(data.payments_enabled))
      .catch(() => setPaymentsEnabled(false));
  }, []);

  // The server decides what this patient may read; a failure leaves the entry
  // hidden, which is the same as the therapist not having shared anything.
  useEffect(() => {
    listPortalSystemicMaps()
      .then((data) => setHasSystemicMaps(data.length > 0))
      .catch(() => setHasSystemicMaps(false));
  }, []);

  // The server decides what this patient was given; a 403 means the therapist
  // is not using the meditation tool, which reads the same as nothing shared.
  useEffect(() => {
    listPortalMeditations()
      .then((data) => setHasMeditations(data.length > 0))
      .catch(() => setHasMeditations(false));
  }, []);

  const sharedItems: NavItem[] = [
    ...(hasMeditations ? [MEDITATIONS_ITEM] : []),
    ...(hasSystemicMaps ? [SYSTEMIC_MAPS_ITEM] : []),
    ...(paymentsEnabled ? [PAYMENTS_ITEM] : []),
  ];
  const moreItems = [...sharedItems, ...ACCOUNT_ITEMS];
  // The more button stands in for every page it opens, so the bar still says
  // where you are once you are on one of them.
  const moreActive = moreItems.some((item) => isOn(pathname, item));

  const handleLogout = async () => {
    try {
      await signOutPatient();
    } finally {
      navigate("/portal/login", { replace: true });
    }
  };

  const sidebarLink = ({ to, label, icon: Icon, end }: NavItem) => (
    <NavLink
      key={to}
      to={to}
      end={end}
      className={({ isActive }) =>
        `menu-item group ${isActive ? "menu-item-active" : "menu-item-inactive"}`
      }
    >
      {({ isActive }) => (
        <>
          <span className={`menu-item-icon-size ${isActive ? "menu-item-icon-active" : "menu-item-icon-inactive"}`}>
            <Icon size={20} />
          </span>
          <span className="menu-item-text font-medium">{t(label)}</span>
        </>
      )}
    </NavLink>
  );

  const tabClass = (active: boolean) =>
    `flex-1 flex flex-col items-center justify-center gap-1 min-h-11 text-xs font-medium transition-colors ${
      active
        ? "text-brand-600 dark:text-brand-400"
        : "text-content-tertiary hover:text-gray-600 dark:hover:text-gray-300"
    }`;

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-950">
      {/* ── Desktop sidebar (xl+) ────────────────────────────── */}
      <aside className={`hidden xl:flex fixed top-0 left-0 h-screen ${SIDEBAR_W} flex-col z-50 glass-effect border-r glass-border px-4 py-6`}>
        <div className="px-2 mb-8">
          <NavLink to="/portal">
            <img src="/images/logo/logo.png" alt="Alchely" className="h-8 w-auto dark:brightness-0 dark:invert" />
          </NavLink>
        </div>
        <nav className="flex-1 flex flex-col gap-1">
          <p className="px-4 mb-3 text-xs font-bold uppercase tracking-[0.1em] text-content-tertiary/80">
            {t("sidebar.menu", "Menú")}
          </p>
          {[...PRIMARY_ITEMS, ...sharedItems].map(sidebarLink)}
          <p className="px-4 mt-6 mb-3 text-xs font-bold uppercase tracking-[0.1em] text-content-tertiary/80">
            {t("portal.nav.account")}
          </p>
          {ACCOUNT_ITEMS.map(sidebarLink)}
        </nav>
      </aside>

      {/* ── Mobile/Tablet top bar (< xl) ─────────────────────── */}
      <div className="xl:hidden sticky top-0 z-40 flex items-center justify-between px-4 h-14 bg-surface-raised border-b border-gray-100 dark:border-white/5">
        <NavLink to="/portal">
          <img src="/images/logo/logo.png" alt="Alchely" className="h-7 w-auto dark:brightness-0 dark:invert" />
        </NavLink>
        <div className="flex items-center gap-1">
          {/* The desktop header had the only language switch, so a patient on
              a phone was stuck with whatever the browser said. */}
          <LanguageSelector />
          <div className="relative">
            <button
              type="button"
              onClick={() => setMobileDropdownOpen((v) => !v)}
              aria-label={patientName ?? t("portal.nav.account")}
              className="dropdown-toggle flex size-11 items-center justify-center"
            >
              <span className="h-8 w-8 rounded-full bg-gray-100 dark:bg-white/5 flex items-center justify-center border border-gray-200 dark:border-white/10">
                <User className="h-4 w-4 text-content-tertiary" />
              </span>
            </button>
            <Dropdown
              isOpen={mobileDropdownOpen}
              onClose={() => setMobileDropdownOpen(false)}
              className="w-48 p-1 right-0"
            >
              {patientName && (
                <p className="px-3 py-2 text-xs text-content-tertiary border-b border-gray-100 dark:border-white/5 truncate">
                  {patientName}
                </p>
              )}
              {/* On a phone the switcher lives in the account menu: the top bar
                  has no room for it, and it is not a per-screen action. */}
              <PortalPanelSwitcher />
              <button
                onClick={() => { setMobileDropdownOpen(false); handleLogout(); }}
                className="flex items-center gap-2 w-full px-3 py-2 text-sm text-content-secondary hover:bg-gray-100 dark:hover:bg-white/5 rounded-lg transition-colors"
              >
                <LogOut className="w-4 h-4" />
                {t("portal.nav.logout")}
              </button>
            </Dropdown>
          </div>
        </div>
      </div>

      {/* ── Content column ───────────────────────────────────── */}
      <div className="xl:ml-[240px] flex flex-col min-h-screen">
        {/* Desktop header */}
        <header className="sticky top-0 z-30 hidden xl:flex items-center justify-end gap-3 px-6 h-16 bg-white/80 dark:bg-gray-950/80 backdrop-blur-md border-b glass-border">
          {/* Only renders when the account holds more than one record. */}
          <PortalPanelSwitcher />
          <LanguageSelector />
          <div className="relative">
            <button
              onClick={() => setDesktopDropdownOpen((v) => !v)}
              className="dropdown-toggle flex items-center gap-2 text-sm font-medium text-content-secondary hover:text-gray-900 dark:hover:text-white transition-colors"
            >
              <div className="h-8 w-8 rounded-full bg-gray-100 dark:bg-white/5 flex items-center justify-center border border-gray-200 dark:border-white/10">
                <User className="h-4 w-4 text-content-tertiary" />
              </div>
              {patientName && <span>{patientName}</span>}
            </button>
            <Dropdown
              isOpen={desktopDropdownOpen}
              onClose={() => setDesktopDropdownOpen(false)}
              className="w-48 p-1"
            >
              <button
                onClick={() => { setDesktopDropdownOpen(false); handleLogout(); }}
                className="flex items-center gap-2 w-full px-3 py-2 text-sm text-content-secondary hover:bg-gray-100 dark:hover:bg-white/5 rounded-lg transition-colors"
              >
                <LogOut className="w-4 h-4" />
                {t("portal.nav.logout")}
              </button>
            </Dropdown>
          </div>
        </header>

        {/* Page content — pb-24 on mobile/tablet to clear bottom nav */}
        <main className="flex-1">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 py-6 pb-24 xl:pb-8">
            {children}
          </div>
        </main>
      </div>

      {/* ── Mobile/Tablet bottom tab bar (< xl) ──────────────── */}
      {/* Four slots whatever the therapist shares: the row holds the pages used
          every week, and one button opens the rest. */}
      <nav
        aria-label={t("portal.nav.tabBar")}
        className="xl:hidden fixed bottom-0 left-0 right-0 z-50 bg-surface-raised border-t border-gray-100 dark:border-white/5 flex items-stretch h-16 safe-area-bottom"
      >
        {PRIMARY_ITEMS.map(({ to, label, icon: Icon, end }) => (
          <NavLink key={to} to={to} end={end} className={({ isActive }) => tabClass(isActive)}>
            <Icon className="w-5 h-5" />
            <span className="leading-none">{t(label)}</span>
          </NavLink>
        ))}
        <button
          type="button"
          onClick={() => setMoreOpen(true)}
          aria-haspopup="dialog"
          aria-expanded={moreOpen}
          aria-current={moreActive ? "page" : undefined}
          className={tabClass(moreActive)}
        >
          <MoreHorizontal className="w-5 h-5" aria-hidden="true" />
          <span className="leading-none">{t("portal.nav.more")}</span>
        </button>
      </nav>

      <Modal
        isOpen={moreOpen}
        onClose={() => setMoreOpen(false)}
        ariaLabel={t("portal.nav.more")}
        className="mx-4 max-w-lg rounded-3xl"
      >
        <div className="flex max-h-[80vh] flex-col gap-5 overflow-y-auto p-5">
          {sharedItems.length > 0 && (
            <MoreSection items={sharedItems} pathname={pathname} onPick={() => setMoreOpen(false)} />
          )}
          <MoreSection
            title={t("portal.nav.account")}
            items={ACCOUNT_ITEMS}
            pathname={pathname}
            onPick={() => setMoreOpen(false)}
          />
        </div>
      </Modal>
    </div>
  );
}

function MoreSection({
  title,
  items,
  pathname,
  onPick,
}: {
  title?: string;
  items: NavItem[];
  pathname: string;
  onPick: () => void;
}) {
  const { t } = useTranslation();
  return (
    <section>
      {title && (
        <h2 className="mb-2 px-1 text-xs font-bold uppercase tracking-[0.1em] text-content-tertiary">
          {title}
        </h2>
      )}
      <ul className="flex flex-col gap-1">
        {items.map((item) => {
          const { to, label, icon: Icon } = item;
          const active = isOn(pathname, item);
          return (
            <li key={to}>
              <NavLink
                to={to}
                onClick={onPick}
                className={`flex min-h-12 items-center gap-3 rounded-xl px-3 text-sm font-medium transition-colors ${
                  active
                    ? "bg-brand-50 text-brand-700 dark:bg-brand-500/10 dark:text-brand-300"
                    : "text-content-primary hover:bg-surface-sunken"
                }`}
              >
                <Icon size={20} className={active ? "" : "text-content-tertiary"} />
                <span className="flex-1">{t(label)}</span>
                <ChevronRight className="h-4 w-4 text-content-tertiary" aria-hidden="true" />
              </NavLink>
            </li>
          );
        })}
      </ul>
    </section>
  );
}
